import { useEffect, useRef, useState } from 'react';
import { formatNumber, type Exercise, type Workout } from '@jungles/shared';
import { api } from '../lib/api';
import { workoutHistoryTitle } from '../lib/workoutTitle';
import { historyCount } from '../lib/historyCount';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { BackLink, ErrorState, Header, JungleStrip, Loader, Screen, TabBar } from '../components/Chrome';
import { historyDate, HistoryRow, useHistory, type HistoryFilter } from '../components/WorkoutHistory';

export function HistoryScreen() {
  const toast = useToast();
  const { data: exercises } = useApi<Exercise[]>('/api/exercises');
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const { page, error, loading, reload, more } = useHistory(filter);
  const [toDelete, setToDelete] = useState<Workout | null>(null);
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (toDelete) dialog.current?.showModal();
    else dialog.current?.close();
  }, [toDelete]);

  const remove = async () => {
    if (!toDelete || busy) return;
    setBusy(true);
    try {
      await api.del(`/api/workouts/${toDelete.id}`);
      setToDelete(null); reload(); toast('Тренировка удалена, очки пересчитаны');
    } catch (err) { toast(err instanceof Error ? err.message : 'Не удалось удалить'); }
    finally { setBusy(false); }
  };
  const months = [...new Set((page?.items ?? []).map(w => w.date.slice(0, 7)))];
  return <Screen tabs>
    <Header /><BackLink to="/workouts" label="Тренировки" /><h1>Все тренировки</h1>
    <div className="history-filters" aria-label="Фильтр тренировок">
      {([['all', 'Все'], ['strength', 'Силовые'], ['cardio', 'Кардио']] as const).map(([value, label]) =>
        <button key={value} type="button" aria-pressed={value === filter} onClick={() => setFilter(value)}>{label}</button>)}
    </div>

    {filter === 'cardio' ? <p className="nt">Бег, ходьба, велосипед, плавание и другие активности</p> : null}
    {months.map(month => {
      const records = page!.items.filter(w => w.date.startsWith(month));
      const total = page!.months.find(m => m.month === month);
      return <section className="history-group" key={month}>
        <div className="history-month"><h2>{historyDate(month, true)}</h2>{total ? <span className="nt">{historyCount(total.count)} · {formatNumber(total.volumeKg)} кг</span> : null}</div>
        {[...new Set(records.map(w => w.date))].map(date => <div key={date}>
          <h3 className="history-day">{historyDate(date)}</h3>
          <div className="wl">{records.filter(w => w.date === date).map(workout => <HistoryRow key={workout.id} workout={workout} exercises={exercises} timezone={page!.timezone} busy={busy} onDelete={() => setToDelete(workout)} />)}</div>
        </div>)}
      </section>;
    })}
    {loading ? <Loader /> : null}
    {error ? <ErrorState message={error} onRetry={page ? () => void more() : reload} /> : null}
    {!loading && !error && page?.items.length === 0 ? <p className="empty">Записей не нашлось</p> : null}
    {page?.nextCursor && !error ? <button className="b wide history-more" disabled={loading} type="button" onClick={() => void more()}>Загрузить ещё</button> : null}
    <JungleStrip /><TabBar />
    <dialog ref={dialog} className="history-confirm" aria-labelledby="history-confirm-title" onCancel={e => { e.preventDefault(); if (!busy) setToDelete(null); }}>
      <h2 id="history-confirm-title">Удалить тренировку?</h2>
      <p>Обновятся минуты, объём и игровые очки. Остальные записи останутся.</p>
      {toDelete ? <p className="nt">{workoutHistoryTitle(toDelete, exercises)} · {historyDate(toDelete.date)}</p> : null}
      <div className="acts"><button autoFocus className="b" type="button" disabled={busy} onClick={() => setToDelete(null)}>Отмена</button><button className="b d" type="button" disabled={busy} onClick={() => void remove()}>Удалить</button></div>
    </dialog>
  </Screen>;
}
